import React, {
  FC, useCallback, useEffect, useRef, useState,
} from 'react';

import cx from 'clsx';
import { Icon } from '@mdi/react';

import { imageOverlayContext, useDelayedUnmount } from 'lib';
import { Overlay } from 'components/molecules';
import { mdiClose } from '@mdi/js';
import styles from './style.module.scss';

export const ImageOverlay: FC = ({ children }) => {

  const srcRef = useRef<string>(null);
  const [active, setActive] = useState(false);

  const src = srcRef.current;
  const render = useDelayedUnmount(active, 250);
  const setSrc = useCallback((newSrc: string) => {

    srcRef.current = newSrc;

    setActive(true);

  }, []);

  const dismiss = useCallback(() => {

    setActive(false);

  }, []);

  const shadowRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  // Dialogs underneath listen for outside clicks on window and would close before this popup does.
  // Handling mousedown here, below window, makes the popup the only thing that reacts while it is open.
  useEffect(() => {

    const shadow = shadowRef.current;

    if (!shadow) return undefined;

    const onMouseDown = (event: MouseEvent) => {

      event.stopPropagation();

      if (!dialogRef.current?.contains(event.target as Node)) dismiss();

    };

    shadow.addEventListener('mousedown', onMouseDown);

    return () => shadow.removeEventListener('mousedown', onMouseDown);

  }, [render, dismiss]);

  // Escape closes only the photo: captured first, so a dialog underneath (the audit log) does not see it.
  useEffect(() => {

    if (!active) return undefined;

    const onKeyDown = (event: KeyboardEvent) => {

      if (event.key !== 'Escape') return;

      event.stopPropagation();
      event.preventDefault();
      dismiss();

    };

    window.addEventListener('keydown', onKeyDown, true);

    return () => window.removeEventListener('keydown', onKeyDown, true);

  }, [active, dismiss]);

  return (
    <imageOverlayContext.Provider value={{
      setSrc,
    }}>
      {render && (
        <div ref={shadowRef} className={cx(styles.shadow, active && styles.active)}>
          <div className={styles.imageContainer}>
            <Overlay
              ref={dialogRef}
              className={styles.dialog}
              onDismiss={dismiss}
            >
              <div className={styles.close} onClick={dismiss}>
                <Icon path={mdiClose}/>
              </div>
              <img src={src} alt={`Popup for (${src})`}/>
            </Overlay>
          </div>
        </div>
      )}
      {children}
    </imageOverlayContext.Provider>
  );

};
